import { InvoiceFilter, InvoicePaymentMethod, InvoiceState, InvoicingSkipReason } from '@/types'

export const statusLabels = {
  draft: 'Brouillon',
  ready: 'Prêt',
  todo: 'À faire',
  to_be_reviewed: 'À vérifier',
  completed: 'Complété',
} as const

export const statusColors = {
  draft: 'gray',
  ready: 'indigo',
  todo: 'blue',
  to_be_reviewed: 'yellow',
  completed: 'green',
} as const

// Weekdays match Ruby's Date#wday (0 = Sunday … 6 = Saturday), displayed Monday-first.
export const weekdays = [
  { value: 1, label: 'Lun' },
  { value: 2, label: 'Mar' },
  { value: 3, label: 'Mer' },
  { value: 4, label: 'Jeu' },
  { value: 5, label: 'Ven' },
  { value: 6, label: 'Sam' },
  { value: 0, label: 'Dim' },
] as const

export const receiptPrintStyles = `
  @page {
    size: 80mm auto;
    margin: 0;
  }
  @media print {
    .no-print,
    header,
    nav,
    aside,
    .mantine-AppShell-header,
    .mantine-AppShell-navbar {
      display: none !important;
    }
    .print-only {
      display: block !important;
      margin: 0 !important;
      padding: 0 !important;
      width: 80mm !important;
    }
    .print-only .receipt {
      break-inside: avoid !important;
      page-break-inside: avoid !important;
      page-break-before: always !important;
      break-before: page !important;
    }
    .print-only .receipt:first-of-type {
      page-break-before: auto !important;
      break-before: auto !important;
    }
    body, html {
      margin: 0 !important;
      padding: 0 !important;
      background: white !important;
      height: auto !important;
      overflow: visible !important;
      width: 80mm !important;
    }
    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
  }
  @media screen {
    .print-only {
      display: none !important;
    }
  }
  .receipt {
    font-family: 'Courier New', Courier, monospace;
    background: white;
    padding: 4mm;
    box-sizing: border-box;
    color: black;
  }
  .receipt-separator {
    border-top: 1px dashed black;
    margin: 8px 0;
  }
`

export const vatRateOptions = [
  { value: '20', label: '20 %' },
  { value: '10', label: '10 %' },
  { value: '5.5', label: '5,5 %' },
  { value: '2.1', label: '2,1 %' },
]


export const invoiceStateLabels: Record<InvoiceState, string> = {
  cancelled: 'Annulée',
  failed: "Échec d'envoi",
  sending: 'Envoi en cours',
  rejected: 'Rejetée',
  refused: 'Refusée',
  paid: 'Encaissée',
  disputed: 'En litige',
  overdue: 'En retard',
  partially_paid: 'Partiellement encaissée',
  sent: 'Transmise',
}

export const invoiceStateColors: Record<InvoiceState, string> = {
  cancelled: 'gray',
  failed: 'red',
  sending: 'blue',
  rejected: 'red',
  refused: 'red',
  paid: 'green',
  disputed: 'orange',
  overdue: 'orange',
  partially_paid: 'teal',
  sent: 'indigo',
}

export const invoiceFilterLabels: Record<InvoiceFilter, string> = {
  all: 'Toutes',
  to_handle: 'À traiter',
  sending: 'Envoi en cours',
  awaiting_payment: 'À encaisser',
  overdue: 'En retard',
  settled: 'Encaissées',
  cancelled: 'Annulées',
}

export const invoicePaymentMethodLabels: Record<InvoicePaymentMethod, string> = {
  transfer: 'Virement',
  check: 'Chèque',
  card: 'Carte',
  cash: 'Espèces',
}

export const invoicingSkipReasonLabels: Record<InvoicingSkipReason, string> = {
  billed_elsewhere: 'Facturé hors SeamFlow',
  free: 'Offert',
  paid_at_counter: 'Réglé au comptoir',
}

// Lifecycle statuses of the French e-invoicing reform (AFNOR XP Z12-012) and of the platform itself.
export const invoiceStatusLabels: Record<string, string> = {
  'api:uploaded': 'Déposée sur la plateforme',
  'api:validated': 'Contrôles de la plateforme passés',
  'api:sent': 'Transmise à la plateforme du client',
  'api:acknowledged': 'Accusé de réception de la plateforme du client',
  'api:accepted': 'Acceptée par la plateforme du client',
  'api:rejected': 'Rejetée par la plateforme du client',
  'api:invalid': 'Invalide',
  'fr:200': 'Déposée',
  'fr:201': 'Émise par la plateforme',
  'fr:202': 'Reçue par la plateforme du client',
  'fr:203': 'Mise à disposition du client',
  'fr:204': 'Prise en charge par le client',
  'fr:205': 'Approuvée par le client',
  'fr:206': 'Approuvée partiellement',
  'fr:207': 'En litige',
  'fr:208': 'Suspendue',
  'fr:209': 'Complétée',
  'fr:210': 'Refusée par le client',
  'fr:211': 'Paiement transmis par le client',
  'fr:212': 'Encaissée',
  'fr:213': 'Rejetée',
  'fr:501': 'Irrecevable',
}

// Statuses that stop an invoice (red) or need the merchant's attention (orange); every other one is a step done.
export const invoiceFailureStatuses = ['api:rejected', 'api:invalid', 'fr:210', 'fr:213', 'fr:501']

export const invoiceWarningStatuses = ['fr:207', 'fr:208']

// Reason codes a client's platform sends with a refusal, a dispute or a rejection (AFNOR XP Z12-012).
export const invoiceReasonLabels: Record<string, string> = {
  ADR_ERR: 'Adresse de facturation électronique erronée',
  CALCUL_ERR: 'Erreur de calcul de la facture',
  CMD_ERR: 'N° de commande incorrect ou manquant',
  CONTRAT_TERM: 'Contrat terminé',
  DEST_ERR: 'Erreur de destinataire',
  DOUBLE_FACT: 'Prestation ou livraison déjà facturée sur une autre facture',
  DOUBLON: 'Facture en doublon',
  EMMET_INC: 'Émetteur inconnu',
  MONTANTTOTAL_ERR: 'Montant total erroné',
  NON_CONFORME: 'Mention légale manquante',
  REF_CT_ABSENT: 'Référence contractuelle manquante',
  TRANSAC_INC: 'Transaction inconnue',
  TX_TVA_ERR: 'Taux de TVA erroné',
  ART_ERR: 'Article erroné',
  AUTRE: 'Autre motif',
  COORD_BANC_ERR: 'Coordonnées bancaires erronées',
  DEST_INC: 'Destinataire inconnu',
  JUSTIF_ABS: 'Justificatif absent',
  LIVR_INCOMP: 'Livraison incomplète',
  MODPAI_ERR: 'Mode de paiement erroné',
  PU_ERR: 'Prix unitaire erroné',
  QTE_ERR: 'Quantité erronée',
  QUALITE_ERR: 'Problème de qualité',
  REF_ERR: 'Référence erronée',
  REM_ERR: 'Remise erronée',
  SIRET_ERR: 'SIRET erroné',
  REJ_SEMAN: 'Erreur sémantique',
  REJ_COH: 'Incohérence entre les données',
  REJ_ADR: "Erreur d'adressage",
  REJ_UNI: 'Facture déjà reçue',
}

// Filings the platform makes to the tax administration (PPF) appear in the same event feed.
export const taxAdministrationFilingLabels: Record<string, string> = {
  validated: 'dépôt déclaré',
  refused: 'refus déclaré',
  rejected: 'rejet déclaré',
  'payment-received': 'encaissement déclaré',
  'flow-1': 'copie de la facture transmise',
}

export const taxAdministrationResponseLabels: Record<string, string> = {
  ack: 'accusé de réception',
  'ack-error': 'erreur',
  rejected: 'rejet',
  'response-ok': 'confirmé',
}

export const invoicingBasisOptions = [
  {
    value: 'deposit',
    label: 'Date de dépôt',
    description: 'Une prestation est facturée le mois où son ticket est déposé, même si elle est encore en cours.',
  },
  {
    value: 'completion',
    label: 'Date de réalisation',
    description: "Une prestation est facturée le mois où l'atelier la termine ; celles en cours attendent ce mois-là.",
  },
] as const

export const vatRegimeLabels = {
  franchise: 'Franchise en base de TVA',
  simplified: 'Réel simplifié',
  normal: 'Réel normal',
} as const

